import React, { useState, useContext } from 'react';
import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  Heading,
  Input,
  Text,
  useToast,
  VStack,
  HStack,
  Divider,
  Box,
  Spinner,
  Image,
} from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { GoogleOAuthProvider } from '@react-oauth/google';
import { FaArrowLeft } from 'react-icons/fa';
import axiosInstance from "../../services/axios";
import PasswordInput from "../../tools/PasswordInput";
import GoogleAuthButton from "./GoogleAuthButton";
import AppleAuthButton from "./AppleAuthButton";
import FacebookAuthButton from "./FacebookAuthButton";
import { AuthContext } from '../../context/JWTAuthContext';
import { useAuth } from '../../hooks/useAuth';
import CustomSelect from '../../utils/CustomSelect';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "your-google-client-id";

const AuthFlow = () => {
  const location = useLocation();
  const getInitialStep = () => {
    if (location.pathname === '/register') return 'register';
    if (location.pathname === '/artist-details') return 'artist-details';
    return 'login';
  };
  const [currentStep, setCurrentStep] = useState(getInitialStep()); // login, register, role-selection, artist-details, otp-verification
  const [userData, setUserData] = useState(null);
  const [googleCredential, setGoogleCredential] = useState(null);
  const [appleCredential, setAppleCredential] = useState(null);
  const [facebookCredential, setFacebookCredential] = useState(null);
  const [selectedRole, setSelectedRole] = useState('');
  const [otpData, setOtpData] = useState(null);
  const [searchParams] = useSearchParams();

  // Mobile redirect detection and handling
  const [isMobileRedirect, setIsMobileRedirect] = useState(false);
  const [mobileReturnUrl, setMobileReturnUrl] = useState(null);
  const [mobileProvider, setMobileProvider] = useState(null);

  // Loading states for better UX
  const [isOAuthLoading, setIsOAuthLoading] = useState(false);
  const [oauthLoadingMessage, setOauthLoadingMessage] = useState('');
  const [isRoleSelectionLoading, setIsRoleSelectionLoading] = useState(false);
  const [isResendingOTP, setIsResendingOTP] = useState(false);
  const [isCompletingProfile, setIsCompletingProfile] = useState(false);

  const navigate = useNavigate();
  const toast = useToast();
  const auth = useAuth();
  const { authenticateWithCookies } = useContext(AuthContext);

  const referralCode = searchParams.get('ref');

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    setValue,
  } = useForm();

  // Main container styling (matches original Login component)
  const containerStyle = {
    alignItems: "center",
    justifyContent: "center",
    minH: "100vh",
    w: "100%",
    px: { base: 2, sm: 4 }
  };

  const formBoxStyle = {
    direction: "column",
    alignItems: "center",
    bg: "dark.500",
    p: { base: 4, sm: 6, md: 8, lg: 12 },
    m: { base: 2, sm: 4, md: 6 },
    rounded: 6,
    maxW: { base: "95vw", sm: "90vw", md: "500px" },
    w: "full",
    sx: {
      WebkitBoxReflect: 'none !important',
      WebkitAppearance: 'none !important',
      '& *': {
        WebkitBoxReflect: 'none !important'
      }
    }
  };

  // Handle mobile redirects from mobile app
  React.useEffect(() => {
    const provider = searchParams.get('provider');
    const credential = searchParams.get('credential');
    const returnUrl = searchParams.get('returnUrl');
    const userData = searchParams.get('userData');
    const source = searchParams.get('source');

    if (source === 'mobile' && provider && returnUrl) {
      console.log('📱 Mobile redirect detected:', { provider, returnUrl });

      setIsMobileRedirect(true);
      setMobileReturnUrl(returnUrl);
      setMobileProvider(provider);

      // Show mobile-specific messaging
      toast({
        title: 'Welcome from Mobile!',
        description: 'Complete your registration below and we\'ll redirect you back to the app.',
        status: 'info',
        duration: 5000,
        isClosable: true,
      });

      if (provider && credential) {
        try {
          const parsedUserData = userData ? JSON.parse(decodeURIComponent(userData)) : {};

          // Auto-trigger social login flow with mobile data
          if (provider === 'google') {
            handleMobileGoogleLogin(decodeURIComponent(credential), parsedUserData);
          } else if (provider === 'apple') {
            handleMobileAppleLogin(decodeURIComponent(credential), parsedUserData);
          } else if (provider === 'facebook') {
            handleMobileFacebookLogin(decodeURIComponent(credential), parsedUserData);
          }
        } catch (error) {
          console.error('Failed to parse mobile redirect data:', error);
          toast({
            title: 'Registration Error',
            description: 'Failed to process mobile registration data. Please try again.',
            status: 'error',
            duration: 5000,
          });
        }
      }
    }
  }, [searchParams, toast]);

  // Mobile completion redirect helper
  const completeMobileRegistration = async (user) => {
    if (isMobileRedirect && mobileReturnUrl) {
      try {
        console.log('🔄 Generating mobile auth code for user:', user.user_id);

        const response = await axiosInstance.post('/auth/generate-mobile-auth-code', {
          userId: user.user_id
        });

        if (response.data && response.data.code) {
          const authCodeUrl = `${mobileReturnUrl}?authCode=${response.data.code}`;

          toast({
            title: 'Registration Complete!',
            description: 'Redirecting back to the mobile app...',
            status: 'success',
            duration: 3000,
          });

          // Redirect to mobile app with auth code
          window.location.href = authCodeUrl;
        } else {
          throw new Error('Failed to generate auth code');
        }
      } catch (error) {
        console.error('Failed to complete mobile registration:', error);
        toast({
          title: 'Redirect Error',
          description: 'Registration successful, but failed to redirect to mobile app. Please open the app manually.',
          status: 'warning',
          duration: 8000,
        });
      }
    }
  };

  // Handle mobile social login flows
  const handleMobileGoogleLogin = async (credential, userData) => {
    console.log('📱 Processing mobile Google login...');
    setGoogleCredential(credential);
    setUserData(userData);
    setCurrentStep('role-selection');
  };

  const handleMobileAppleLogin = async (credential, userData) => {
    console.log('📱 Processing mobile Apple login...');
    setAppleCredential(credential);
    setUserData(userData);
    setCurrentStep('role-selection');
  };

  const handleMobileFacebookLogin = async (credential, userData) => {
    console.log('📱 Processing mobile Facebook login...');
    setFacebookCredential(credential);
    setUserData(userData);
    setCurrentStep('role-selection');
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setIsOAuthLoading(true);
    setOauthLoadingMessage('Signing in with Google...');

    try {
      const response = await axiosInstance.post('/auth/google-signin', {
        credential: credentialResponse.credential
      });

      if (response.data.status === 'existing_user') {
        // Clear Google credential for existing users
        setGoogleCredential(null);

        setOauthLoadingMessage('Authenticating...');
        await authenticateWithCookies();

        // Check if the existing user is an incomplete artist or merchant
        try {
          setOauthLoadingMessage('Loading your profile...');
          const userResponse = await axiosInstance.get('/users/me');
          const user = userResponse.data;

          if (user.account_type === 'artist' && user.profile_completion_status === 'incomplete') {
            toast({
              title: 'Complete your artist profile',
              description: 'Please complete your artist profile to access the platform',
              status: 'warning',
              duration: 3000,
            });
            setOauthLoadingMessage('Redirecting...');
            setTimeout(() => {
              navigate('/artist-details', { replace: true });
              setIsOAuthLoading(false);
            }, 500);
          } else if (user.account_type === 'merchant' && user.profile_completion_status !== 'complete') {
            toast({
              title: 'Complete your company profile',
              description: 'Please complete your company information to access the platform',
              status: 'warning',
              duration: 3000,
            });
            setOauthLoadingMessage('Redirecting...');
            setTimeout(() => {
              navigate('/merchant/company-info', { replace: true });
              setIsOAuthLoading(false);
            }, 500);
          } else {
            toast({
              title: 'Welcome back!',
              description: 'Redirecting to home page...',
              status: 'success',
              duration: 2000,
            });
            setOauthLoadingMessage('Redirecting...');
            setTimeout(() => {
              navigate('/', { replace: true });
              setIsOAuthLoading(false);
            }, 500);
          }
        } catch (err) {
          console.error('Failed to fetch user data after login:', err);
          setOauthLoadingMessage('Redirecting...');
          setTimeout(() => {
            navigate('/', { replace: true });
            setIsOAuthLoading(false);
          }, 500);
        }
      } else {
        // New user - store data and go to role selection
        setUserData(response.data.user_data);
        setGoogleCredential(credentialResponse.credential);
        setIsOAuthLoading(false);
        setCurrentStep('role-selection');
      }
    } catch (error) {
      console.error('Google sign-in error:', error);
      setIsOAuthLoading(false);
      toast({
        title: 'Authentication failed',
        description: error.response?.data?.detail || 'Please try again',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleAppleSuccess = async (appleResponse) => {
    setIsOAuthLoading(true);
    setOauthLoadingMessage('Signing in with Apple...');

    try {
      const response = await axiosInstance.post('/auth/apple-signin', {
        identity_token: appleResponse.authorization.id_token
      });

      if (response.data.status === 'existing_user') {
        setOauthLoadingMessage('Authenticating...');
        await authenticateWithCookies();

        // Check if the existing user is an incomplete artist or merchant
        try {
          setOauthLoadingMessage('Loading your profile...');
          const userResponse = await axiosInstance.get('/users/me');
          const user = userResponse.data;

          if (user.account_type === 'artist' && user.profile_completion_status === 'incomplete') {
            toast({
              title: 'Complete your artist profile',
              description: 'Please complete your artist profile to access the platform',
              status: 'warning',
              duration: 3000,
            });
            setOauthLoadingMessage('Redirecting...');
            setTimeout(() => {
              navigate('/artist-details', { replace: true });
              setIsOAuthLoading(false);
            }, 500);
          } else if (user.account_type === 'merchant' && user.profile_completion_status !== 'complete') {
            toast({
              title: 'Complete your company profile',
              description: 'Please complete your company information to access the platform',
              status: 'warning',
              duration: 3000,
            });
            setOauthLoadingMessage('Redirecting...');
            setTimeout(() => {
              navigate('/merchant/company-info', { replace: true });
              setIsOAuthLoading(false);
            }, 500);
          } else {
            toast({
              title: 'Welcome back!',
              description: 'Redirecting to home page...',
              status: 'success',
              duration: 2000,
            });
            setOauthLoadingMessage('Redirecting...');
            setTimeout(() => {
              navigate('/', { replace: true });
              setIsOAuthLoading(false);
            }, 500);
          }
        } catch (err) {
          console.error('Failed to fetch user data after login:', err);
          setOauthLoadingMessage('Redirecting...');
          setTimeout(() => {
            navigate('/', { replace: true });
            setIsOAuthLoading(false);
          }, 500);
        }
      } else {
        // New user - store data and go to role selection
        setUserData(response.data.user_data);
        setAppleCredential(appleResponse.authorization.id_token);
        setIsOAuthLoading(false);
        setCurrentStep('role-selection');
      }
    } catch (error) {
      console.error('Apple sign-in error:', error);
      setIsOAuthLoading(false);
      toast({
        title: 'Apple Authentication failed',
        description: error.response?.data?.detail || 'Please try again',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleFacebookSuccess = async (facebookResponse) => {
    setIsOAuthLoading(true);
    setOauthLoadingMessage('Signing in with Facebook...');

    try {
      const response = await axiosInstance.post('/auth/facebook-signin', {
        access_token: facebookResponse.accessToken
      });

      if (response.data.status === 'existing_user') {
        setOauthLoadingMessage('Authenticating...');
        await authenticateWithCookies();

        // Check if the existing user is an incomplete artist or merchant
        try {
          setOauthLoadingMessage('Loading your profile...');
          const userResponse = await axiosInstance.get('/users/me');
          const user = userResponse.data;

          if (user.account_type === 'artist' && user.profile_completion_status === 'incomplete') {
            toast({
              title: 'Complete your artist profile',
              description: 'Please complete your artist profile to access the platform',
              status: 'warning',
              duration: 3000,
            });
            setOauthLoadingMessage('Redirecting...');
            setTimeout(() => {
              navigate('/artist-details', { replace: true });
              setIsOAuthLoading(false);
            }, 500);
          } else if (user.account_type === 'merchant' && user.profile_completion_status !== 'complete') {
            toast({
              title: 'Complete your company profile',
              description: 'Please complete your company information to access the platform',
              status: 'warning',
              duration: 3000,
            });
            setOauthLoadingMessage('Redirecting...');
            setTimeout(() => {
              navigate('/merchant/company-info', { replace: true });
              setIsOAuthLoading(false);
            }, 500);
          } else {
            toast({
              title: 'Welcome back!',
              description: 'Redirecting to home page...',
              status: 'success',
              duration: 2000,
            });
            setOauthLoadingMessage('Redirecting...');
            setTimeout(() => {
              navigate('/', { replace: true });
              setIsOAuthLoading(false);
            }, 500);
          }
        } catch (err) {
          console.error('Failed to fetch user data after login:', err);
          setOauthLoadingMessage('Redirecting...');
          setTimeout(() => {
            navigate('/', { replace: true });
            setIsOAuthLoading(false);
          }, 500);
        }
      } else {
        // New user - store data and go to role selection
        setUserData(response.data.user_data);
        setFacebookCredential(facebookResponse.accessToken);
        setIsOAuthLoading(false);
        setCurrentStep('role-selection');
      }
    } catch (error) {
      console.error('Facebook sign-in error:', error);
      setIsOAuthLoading(false);
      toast({
        title: 'Facebook Authentication failed',
        description: error.response?.data?.detail || 'Please try again',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const renderLoginStep = () => (
    <>
      <Heading mb={6} size="md" color="dark.900">
        Login
      </Heading>

      <HStack
        spacing={4}
        width="100%"
        justify="center"
        align="center"
        opacity={isOAuthLoading || isSubmitting ? 0.5 : 1}
        pointerEvents={isOAuthLoading || isSubmitting ? 'none' : 'auto'}
        sx={{
          WebkitBoxReflect: 'none !important',
          '& > *': {
            WebkitBoxReflect: 'none !important'
          }
        }}
      >
        <Box sx={{ WebkitBoxReflect: 'none !important', WebkitAppearance: 'none !important' }}>
          <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
            <GoogleAuthButton onSuccess={handleGoogleSuccess} isLoading={isOAuthLoading} />
          </GoogleOAuthProvider>
        </Box>

        <AppleAuthButton onSuccess={handleAppleSuccess} isLoading={isOAuthLoading} />

        <FacebookAuthButton onSuccess={handleFacebookSuccess} isLoading={isOAuthLoading} />
      </HStack>

      <HStack spacing={3} width="100%" my={4}>
        <Divider />
        <Text fontSize="sm" color="primary.100" whiteSpace="nowrap">or continue with email</Text>
        <Divider />
      </HStack>

      <form onSubmit={handleSubmit((values) => {
        // Handle manual login
        const formData = new FormData();
        formData.append("username", values.email);
        formData.append("password", values.password);

        axiosInstance.post("/auth/login_otp", formData)
          .then(async response => {
            if (response.data.status_code === "200") {
              setOtpData({
                userId: response.data.user_id,
                email: response.data.email,
                phone: response.data.phone
              });
              setCurrentStep('otp-verification');
            } else {
              // User logged in successfully without OTP, check profile completion
              try {
                await authenticateWithCookies();
                const userResponse = await axiosInstance.get('/users/me');
                const user = userResponse.data;

                if (user.account_type === 'artist' && user.profile_completion_status === 'incomplete') {
                  toast({
                    title: 'Complete your artist profile',
                    description: 'Please complete your artist profile to access the platform',
                    status: 'warning',
                    duration: 3000,
                  });
                  navigate('/artist-details', { replace: true });
                } else if (user.account_type === 'merchant' && user.profile_completion_status !== 'complete') {
                  toast({
                    title: 'Complete your company profile',
                    description: 'Please complete your company information to access the platform',
                    status: 'warning',
                    duration: 3000,
                  });
                  navigate('/merchant/company-info', { replace: true });
                } else {
                  navigate('/', { replace: true });
                }
              } catch (err) {
                console.error('Failed to check user status after login:', err);
                navigate('/', { replace: true });
              }
            }
          })
          .catch(() => {
            toast({
              title: "Invalid email or password",
              status: "error",
              isClosable: true,
              duration: 1500,
            });
          });
      })}>
        <FormControl isInvalid={errors.email}>
          <Input
            placeholder="Email"
            type="email"
            size="md"
            variant="outline"
            mt={3}
            {...register("email", {
              required: "Email is required",
            })}
          />
          <FormErrorMessage>{errors.email && errors.email.message}</FormErrorMessage>
        </FormControl>

        <PasswordInput
          register={register}
          errors={errors}
          name="password"
          placeholder="Password"
          variant="outline"
          validation={{ required: "Password is required" }}
        />

        <Button
          isLoading={isSubmitting}
          loadingText="Logging in..."
          width="100%"
          variant="solid"
          size="md"
          mt={6}
          type="submit"
        >
          Sign in
        </Button>
      </form>

      <Button
        onClick={() => {
          setCurrentStep('register');
          navigate('/register', { replace: true });
        }}
        width="100%"
        variant="ghost"
        size="md"
        mt={4}
      >
        Register Instead
      </Button>

      <Text mt={4} textAlign="center" fontSize="sm" color="primary.100">
        forgot password?{" "}
        <Text as={Link} to="/reset-password" textDecor="underline" color="dark.900">
          reset
        </Text>
      </Text>
    </>
  );

  const renderRegisterStep = () => (
    <>
      <Heading mb={6} size="md" color="dark.900">
        Register
      </Heading>

      <Button
        onClick={() => {
          setCurrentStep('login');
          navigate('/login', { replace: true });
        }}
        variant="ghost"
        size="md"
        color="dark.900"
        mb={6}
        leftIcon={<FaArrowLeft />}
      >
        Back to Login
      </Button>

      <HStack
        spacing={4}
        width="100%"
        justify="center"
        align="center"
        opacity={isOAuthLoading || isSubmitting ? 0.5 : 1}
        pointerEvents={isOAuthLoading || isSubmitting ? 'none' : 'auto'}
        sx={{
          WebkitBoxReflect: 'none !important',
          '& > *': {
            WebkitBoxReflect: 'none !important'
          }
        }}
      >
        <Box sx={{ WebkitBoxReflect: 'none !important', WebkitAppearance: 'none !important' }}>
          <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
            <GoogleAuthButton onSuccess={handleGoogleSuccess} isLoading={isOAuthLoading} />
          </GoogleOAuthProvider>
        </Box>

        <AppleAuthButton onSuccess={handleAppleSuccess} isLoading={isOAuthLoading} />

        <FacebookAuthButton onSuccess={handleFacebookSuccess} isLoading={isOAuthLoading} />
      </HStack>

      <HStack spacing={3} width="100%" my={4}>
        <Divider />
        <Text fontSize="sm" color="primary.100" whiteSpace="nowrap">or register with email</Text>
        <Divider />
      </HStack>

      <form onSubmit={handleSubmit(async (values) => {
        try {
          const response = await axiosInstance.post("/auth/register", {
            username: values.username || null,
            firstname: values.firstname,
            lastname: values.lastname,
            email: values.email,
            password: values.password,
            account_type: "fan", // Default to fan, will be changed in role selection
            account: { profile: null },
            code_referred: values.referralCode || null
          });

          setOtpData({
            userId: response.data.user_id,
            email: response.data.email,
            phone: response.data.phone || ''
          });
          setUserData(response.data);
          setCurrentStep('otp-verification');
        } catch (error) {
          toast({
            title: "Registration failed",
            description: error.response?.data?.detail || "Please try again",
            status: "error",
            duration: 3000,
          });
        }
      })}>
        <FormControl isInvalid={errors.firstname}>
          <Input
            placeholder="First Name"
            {...register("firstname", {
              required: "First name is required",
              minLength: { value: 3, message: "Minimum 3 characters" }
            })}
            size="md"
            variant="outline"
            mt={3}
          />
          <FormErrorMessage>
            {errors.firstname && errors.firstname.message}
          </FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={errors.lastname}>
          <Input
            placeholder="Last Name"
            {...register("lastname", {
              required: "Last name is required",
              minLength: { value: 3, message: "Minimum 3 characters" }
            })}
            size="md"
            variant="outline"
            mt={3}
          />
          <FormErrorMessage>
            {errors.lastname && errors.lastname.message}
          </FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={errors.email}>
          <Input
            placeholder="Email"
            type="email"
            size="md"
            variant="outline"
            mt={3}
            {...register("email", {
              required: "Email is required",
              pattern: {
                value: /^\S+@\S+$/i,
                message: "Invalid email address",
              },
            })}
          />
          <FormErrorMessage>{errors.email && errors.email.message}</FormErrorMessage>
        </FormControl>

        <PasswordInput
          register={register}
          errors={errors}
          name="password"
          placeholder="Password"
          variant="outline"
          validation={{
            required: "Password is required",
            minLength: { value: 5, message: "Minimum 5 characters" }
          }}
        />

        <PasswordInput
          register={register}
          errors={errors}
          name="confirmPassword"
          placeholder="Confirm Password"
          variant="outline"
          validation={{
            required: "Please confirm your password",
            validate: (value, formValues) => {
              return value === formValues.password || "Passwords do not match";
            }
          }}
        />

        <FormControl>
          <Input
            placeholder="Username (optional for fans)"
            {...register("username", {
              minLength: { value: 5, message: "Minimum 5 characters if provided" }
            })}
            size="md"
            variant="outline"
            mt={3}
          />
          <Text fontSize="xs" color="dark.900" mt={1}>
            You can set this later from your profile settings
          </Text>
        </FormControl>

        <FormControl>
          <Input
            placeholder="Referral Code (optional)"
            {...register("referralCode")}
            defaultValue={referralCode || ''}
            size="md"
            variant="outline"
            mt={3}
          />
        </FormControl>

        <Button
          type="submit"
          width="100%"
          variant="solid"
          size="md"
          mt={6}
          isLoading={isSubmitting}
          loadingText="Creating Account..."
        >
          Register
        </Button>
      </form>
    </>
  );

  const renderRoleSelectionStep = () => (
    <>
      <Heading mb={6} size="md" color="dark.900">
        Choose Your Role
      </Heading>

      <Text color="primary.100" mb={6} textAlign="center">
        How do you plan to use the platform?
      </Text>

      <Button
        width="100%"
        variant={selectedRole === 'fan' ? 'solid' : 'outline'}
        borderColor="dark.900"
        bg={selectedRole === 'fan' ? 'dark.500' : 'transparent'}
        color={selectedRole === 'fan' ? 'primary.100' : 'dark.900'}
        onClick={() => setSelectedRole('fan')}
        mb={4}
        py={6}
        _hover={{
          bg: selectedRole === 'fan' ? 'dark.100' : 'dark.500',
          color: 'primary.100',
          borderColor: 'dark.900',
          transform: 'translateY(-2px)',
          shadow: 'lg'
        }}
        transition="all 0.3s ease"
      >
        <VStack spacing={1}>
          <Text fontWeight="bold" color={selectedRole === 'fan' ? 'primary.100' : 'dark.900'}>Fan</Text>
          <Text fontSize="sm" opacity={0.8} color={selectedRole === 'fan' ? 'primary.900' : 'primary.900'}>
            Discover and invest in music tracks
          </Text>
        </VStack>
      </Button>

      <Button
        width="100%"
        variant={selectedRole === 'artist' ? 'solid' : 'outline'}
        borderColor="dark.900"
        bg={selectedRole === 'artist' ? 'dark.500' : 'transparent'}
        color={selectedRole === 'artist' ? 'primary.100' : 'dark.900'}
        onClick={() => setSelectedRole('artist')}
        mb={4}
        py={6}
        _hover={{
          bg: selectedRole === 'artist' ? 'dark.100' : 'dark.500',
          color: 'primary.100',
          borderColor: 'dark.900',
          transform: 'translateY(-2px)',
          shadow: 'lg'
        }}
        transition="all 0.3s ease"
      >
        <VStack spacing={1}>
          <Text fontWeight="bold" color={selectedRole === 'artist' ? 'primary.100' : 'dark.900'}>Artist</Text>
          <Text fontSize="sm" opacity={0.8} color={selectedRole === 'artist' ? 'primary.900' : 'primary.900'}>
            Share your music and build a fanbase
          </Text>
        </VStack>
      </Button>

      <Button
        width="100%"
        variant={selectedRole === 'merchant' ? 'solid' : 'outline'}
        borderColor="dark.900"
        bg={selectedRole === 'merchant' ? 'dark.500' : 'transparent'}
        color={selectedRole === 'merchant' ? 'primary.100' : 'dark.900'}
        onClick={() => setSelectedRole('merchant')}
        mb={6}
        py={6}
        _hover={{
          bg: selectedRole === 'merchant' ? 'dark.100' : 'dark.500',
          color: 'primary.100',
          borderColor: 'dark.900',
          transform: 'translateY(-2px)',
          shadow: 'lg'
        }}
        transition="all 0.3s ease"
      >
        <VStack spacing={1}>
          <Text fontWeight="bold" color={selectedRole === 'merchant' ? 'primary.100' : 'dark.900'}>Merchant</Text>
          <Text fontSize="sm" opacity={0.8} color={selectedRole === 'merchant' ? 'primary.900' : 'primary.900'}>
            Manage multiple artists and business operations
          </Text>
        </VStack>
      </Button>

      <Button
        width="100%"
        variant="solid"
        size="md"
        isDisabled={!selectedRole || isRoleSelectionLoading}
        isLoading={isRoleSelectionLoading}
        loadingText={`Setting up your ${selectedRole} account...`}
        mt={6}
        onClick={async () => {
          if (selectedRole === 'fan') {
            setIsRoleSelectionLoading(true);
            try {
              if (googleCredential) {
                // Complete Google OAuth with fan role
                await axiosInstance.post('/auth/google-role-selection', {
                  credential: googleCredential,
                  account_type: 'fan'
                });
              } else if (appleCredential) {
                // Complete Apple Sign-In with fan role
                await axiosInstance.post('/auth/apple-role-selection', {
                  identity_token: appleCredential,
                  account_type: 'fan'
                });
              } else if (facebookCredential) {
                // Complete Facebook Login with fan role
                await axiosInstance.post('/auth/facebook-role-selection', {
                  access_token: facebookCredential,
                  account_type: 'fan'
                });
              } else if (userData) {
                // Manual registration - use manual role selection
                await axiosInstance.post('/auth/manual-role-selection', {
                  user_id: userData.user_id,
                  account_type: 'fan'
                });
              }
              await authenticateWithCookies();

              // Check if this is a mobile registration completion
              try {
                const userResponse = await axiosInstance.get('/users/me');
                const user = userResponse.data;

                if (isMobileRedirect) {
                  await completeMobileRegistration(user);
                } else {
                  toast({
                    title: 'Welcome to the platform!',
                    status: 'success',
                    duration: 3000,
                  });
                  setTimeout(() => {
                    navigate('/', { replace: true });
                    setIsRoleSelectionLoading(false);
                  }, 500);
                }
              } catch (err) {
                console.error('Failed to get user after role selection:', err);
                toast({
                  title: 'Welcome to the platform!',
                  status: 'success',
                  duration: 3000,
                });
                setTimeout(() => {
                  navigate('/', { replace: true });
                  setIsRoleSelectionLoading(false);
                }, 500);
              }
            } catch (err) {
              setIsRoleSelectionLoading(false);
              toast({
                title: 'Something went wrong',
                description: 'Please try again',
                status: 'error',
                duration: 3000,
              });
            }
          } else if (selectedRole === 'artist') {
            setIsRoleSelectionLoading(true);
            // Artist selected
            if (userData && !googleCredential && !appleCredential && !facebookCredential) {
              // Manual registration - update role first, then go to artist details
              try {
                await axiosInstance.post('/auth/manual-role-selection', {
                  user_id: userData.user_id,
                  account_type: 'artist'
                });
                setIsRoleSelectionLoading(false);
                setCurrentStep('artist-details');
              } catch (err) {
                setIsRoleSelectionLoading(false);
                toast({
                  title: 'Something went wrong',
                  description: 'Please try again',
                  status: 'error',
                  duration: 3000,
                });
              }
            } else {
              // OAuth (Google, Apple, or Facebook) - go directly to artist details (role will be set there)
              setIsRoleSelectionLoading(false);
              setCurrentStep('artist-details');
            }
          } else if (selectedRole === 'merchant') {
            setIsRoleSelectionLoading(true);
            // Merchant selected
            try {
              if (googleCredential) {
                // Complete Google OAuth with merchant role
                await axiosInstance.post('/auth/google-role-selection', {
                  credential: googleCredential,
                  account_type: 'merchant'
                });
                // Redirect to company info step
                window.location.href = '/merchant/company-info';
              } else if (appleCredential) {
                // Complete Apple Sign-In with merchant role
                await axiosInstance.post('/auth/apple-role-selection', {
                  identity_token: appleCredential,
                  account_type: 'merchant'
                });
                // Redirect to company info step
                window.location.href = '/merchant/company-info';
              } else if (facebookCredential) {
                // Complete Facebook Login with merchant role
                await axiosInstance.post('/auth/facebook-role-selection', {
                  access_token: facebookCredential,
                  account_type: 'merchant'
                });
                // Redirect to company info step
                window.location.href = '/merchant/company-info';
              } else if (userData) {
                // Manual registration - use manual role selection
                await axiosInstance.post('/auth/manual-role-selection', {
                  user_id: userData.user_id,
                  account_type: 'merchant'
                });
                // Redirect to company info step
                window.location.href = '/merchant/company-info';
              }
            } catch (err) {
              setIsRoleSelectionLoading(false);
              toast({
                title: 'Something went wrong',
                description: 'Please try again',
                status: 'error',
                duration: 3000,
              });
            }
          }
        }}
      >
        Continue as {selectedRole === 'fan' ? 'Fan' : selectedRole === 'artist' ? 'Artist' : 'Merchant'}
      </Button>
    </>
  );

  // Artist Details Step (with Spotify/Apple Music integration)
  const [artistId, setArtistId] = useState('');
  const [platform, setPlatform] = useState('');
  const [storeFront, setStoreFront] = useState('');
  const [loading, setLoading] = useState(false);
  const [artistData, setArtistData] = useState(null);
  const [artistReferralCode, setArtistReferralCode] = useState(referralCode || '');

  const fetchArtistDetails = async () => {
    setLoading(true);
    // Clear previous artist data
    setArtistData(null);

    try {
      if (!artistId || !platform) {
        setLoading(false);
        throw new Error('Please enter Artist Id and select a platform');
      }

      let endpoint = '';
      let response = null;
      let data = null;

      switch (platform) {
        case 'spotify':
          endpoint = `/spotify/artist_info/${artistId}`;
          response = await axiosInstance.get(endpoint);

          console.log('Spotify API response:', response.data);
          console.log('Spotify genres:', response.data.genres);

          // Validate Spotify data
          if (!response.data.name || !response.data.name.trim()) {
            throw new Error('Artist name not found in Spotify data');
          }

          // Allow empty genres - artists can add them later from settings
          const spotifyGenres = response.data.genres || [];

          data = {
            artist_name: response.data.name,
            genres: spotifyGenres,
            artist_id: {
              id: response.data.id,
              platform: platform,
            },
            image_url: response.data.images[0].url,
          };
          break;
        case 'apple':
          if (!storeFront) {
            throw new Error('Please enter Store Front for Apple Music');
          }
          endpoint = `/apple/artist/${storeFront}/${artistId}`;
          response = await axiosInstance.get(endpoint);

          console.log('Apple Music API response:', response.data);
          console.log('Apple Music genres:', response.data.data[0].attributes.genreNames);

          // Validate Apple Music data
          if (!response.data.data[0].attributes.name || !response.data.data[0].attributes.name.trim()) {
            throw new Error('Artist name not found in Apple Music data');
          }

          // Allow empty genres - artists can add them later from settings
          const appleGenres = response.data.data[0].attributes.genreNames || [];

          data = {
            artist_name: response.data.data[0].attributes.name,
            genres: appleGenres,
            artist_id: {
              id: response.data.data[0].id,
              platform: platform,
            },
            image_url: `${response.data.data[0].attributes.artwork.url.slice(0, response.data.data[0].attributes.artwork.url.length - 14)}/${response.data.data[0].attributes.artwork.width}x${response.data.data[0].attributes.artwork.height}bb.jpg`,
          };
          break;
        default:
          throw new Error('Invalid platform');
      }

      console.log('Setting artist data:', data);
      setArtistData(data);
    } catch (error) {
      // Clear artist data on error
      setArtistData(null);

      toast({
        title: 'Error Fetching Artist Details',
        description: error.message,
        status: 'error',
        isClosable: true,
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const renderArtistDetailsStep = () => (
    <>
      <Heading mb={6} size="md" color="dark.900">
        Complete Your Artist Profile
      </Heading>

      <Text color="dark.900" mb={6} textAlign="center">
        Verify your artist profile using Spotify or Apple Music
      </Text>

      {/* Platform Selection */}
      <FormControl isRequired>
        <CustomSelect
          placeholder="Select Platform"
          value={platform}
          onChange={setPlatform}
          width="100%"
        >
          <option value="spotify">Spotify</option>
          <option value="apple">Apple Music</option>
        </CustomSelect>
      </FormControl>

      {/* Artist ID Input */}
      <FormControl isRequired>
        <Input
          placeholder="Artist Id"
          type="text"
          variant="outline"
          size="md"
          mt={3}
          value={artistId}
          onChange={(e) => setArtistId(e.target.value)}
        />
      </FormControl>

      {/* Store Front for Apple Music */}
      {platform === 'apple' && (
        <FormControl isRequired>
          <Input
            placeholder={`Enter Store Front for ${platform}`}
            type="text"
            variant="outline"
            size="md"
            mt={3}
            value={storeFront}
            onChange={(e) => setStoreFront(e.target.value)}
          />
        </FormControl>
      )}

      {/* Referral Code */}
      <FormControl>
        <Input
          placeholder="Referral Code (optional)"
          type="text"
          variant="outline"
          size="md"
          mt={3}
          value={artistReferralCode}
          onChange={(e) => setArtistReferralCode(e.target.value)}
        />
      </FormControl>

      {/* Fetch Artist Info Button */}
      <Button
        onClick={fetchArtistDetails}
        width="100%"
        variant="solid"
        mt={6}
        isLoading={loading}
        loadingText="Fetching..."
      >
        Get Artist Info
      </Button>

      {/* Artist Data Display */}
      {artistData && (
        <Box mt={6} bg="secondary.500" p={4} rounded={6}>
          <Image
            my={2}
            src={artistData.image_url}
            alt={artistData.artist_name}
            borderRadius='lg'
            width="100px"
            mx="auto"
          />

          <FormControl mt={3}>
            <Input
              placeholder="Artist Name"
              type="text"
              variant="outline"
              value={artistData.artist_name}
              isReadOnly
            />
          </FormControl>

          <FormControl mt={3}>
            <Input
              placeholder="Genres"
              type="text"
              variant="outline"
              value={artistData.genres.length > 0 ? artistData.genres.join(", ") : "No genres listed - you can add them later from settings"}
              isReadOnly
            />
          </FormControl>

          <FormControl mt={3}>
            <Input
              placeholder="Artist Id"
              type="text"
              variant="outline"
              value={artistData.artist_id.id}
              isReadOnly
            />
          </FormControl>

          {/* Complete Profile Button */}
          <Button
            isDisabled={
              !artistData ||
              !artistData.artist_name
            }
            onClick={async () => {
              setIsCompletingProfile(true);
              try {
                // Validate artist data before submitting
                if (!artistData.artist_name || artistData.artist_name.trim().length === 0) {
                  throw new Error('Artist name is required');
                }

                // Handle OAuth role selection only for new users from OAuth flow
                if (!auth.isAuthenticated) {
                  if (googleCredential) {
                    // This is a new Google OAuth user - complete role selection first
                    await axiosInstance.post('/auth/google-role-selection', {
                      credential: googleCredential,
                      account_type: 'artist'
                    });
                    await authenticateWithCookies();
                  } else if (appleCredential) {
                    // This is a new Apple Sign-In user - complete role selection first
                    await axiosInstance.post('/auth/apple-role-selection', {
                      identity_token: appleCredential,
                      account_type: 'artist'
                    });
                    await authenticateWithCookies();
                  } else if (facebookCredential) {
                    // This is a new Facebook Login user - complete role selection first
                    await axiosInstance.post('/auth/facebook-role-selection', {
                      access_token: facebookCredential,
                      account_type: 'artist'
                    });
                    await authenticateWithCookies();
                  }
                }
                // If user is already authenticated, skip OAuth role selection
                // (they're completing profile after being redirected by the system)

                // Complete artist profile with fetched data
                const profileData = {
                  artist_name: artistData.artist_name.trim(),
                  genres: Array.isArray(artistData.genres) ? artistData.genres : []
                };
                console.log('Sending artist profile data:', JSON.stringify(profileData, null, 2));
                console.log('Artist name:', profileData.artist_name);
                console.log('Genres:', profileData.genres);
                console.log('Genres is array:', Array.isArray(profileData.genres));
                console.log('Genres length:', profileData.genres.length);

                const response = await axiosInstance.put('/users/complete-artist-profile', profileData);
                console.log('Profile completion response:', response.data);

                // Refresh authentication to get updated profile status
                await authenticateWithCookies();

                // Check if this is a mobile registration completion
                try {
                  const userResponse = await axiosInstance.get('/users/me');
                  const user = userResponse.data;

                  if (isMobileRedirect) {
                    await completeMobileRegistration(user);
                  } else {
                    toast({
                      title: 'Artist profile completed!',
                      description: 'Welcome to the platform',
                      status: 'success',
                      duration: 3000,
                    });
                    setTimeout(() => {
                      navigate('/', { replace: true });
                      setIsCompletingProfile(false);
                    }, 500);
                  }
                } catch (err) {
                  console.error('Failed to get user after artist profile completion:', err);
                  toast({
                    title: 'Artist profile completed!',
                    description: 'Welcome to the platform',
                    status: 'success',
                    duration: 3000,
                  });
                  setTimeout(() => {
                    navigate('/', { replace: true });
                    setIsCompletingProfile(false);
                  }, 500);
                }

              } catch (err) {
                console.error('Failed to complete artist profile:', err);
                console.error('Error response:', err.response);
                console.error('Error response data:', err.response?.data);
                console.error('Error response detail:', JSON.stringify(err.response?.data?.detail, null, 2));
                setIsCompletingProfile(false);

                // Handle different types of errors
                let errorMessage = 'Please try again';

                // Handle validation errors from our own checks
                if (err.message && !err.response) {
                  errorMessage = err.message;
                }
                // Handle backend validation errors
                else if (err.response?.data?.detail) {
                  if (typeof err.response.data.detail === 'string') {
                    errorMessage = err.response.data.detail;
                  } else if (Array.isArray(err.response.data.detail)) {
                    // Pydantic validation errors
                    const errors = err.response.data.detail.map(e => {
                      const field = e.loc ? e.loc[e.loc.length - 1] : 'field';
                      return `${field}: ${e.msg}`;
                    });
                    errorMessage = errors.join('; ');
                  }
                }

                toast({
                  title: 'Failed to save profile',
                  description: errorMessage,
                  status: 'error',
                  duration: 5000,
                  isClosable: true,
                });
              }
            }}
            width="100%"
            variant="solid"
            size="md"
            isLoading={isCompletingProfile}
            loadingText="Completing Profile..."
            mt={6}
          >
            Complete Artist Profile
          </Button>
        </Box>
      )}
    </>
  );

  const renderOTPStep = () => (
    <>
      <Heading mb={6} size={{ base: "sm", md: "md" }} color="dark.900">
        Verify Your Account
      </Heading>

      <Text color="dark.900" mb={6} textAlign="center" fontSize={{ base: "sm", md: "md" }} px={2}>
        Enter the verification code sent to {otpData?.email}
      </Text>

      <form onSubmit={handleSubmit(async (values) => {
        try {
          await axiosInstance.post(`/auth/verify/otp/${otpData.userId}/${values.otp}`);

          toast({
            title: 'Account verified!',
            status: 'success',
            duration: 2000,
          });

          // After OTP verification, check if it's login or registration flow
          if (userData) {
            // This is from registration flow - go to role selection
            setCurrentStep('role-selection');
          } else {
            // This is from login flow - check profile completion and redirect appropriately
            try {
              await authenticateWithCookies();
              const userResponse = await axiosInstance.get('/users/me');
              const user = userResponse.data;

              if (user.account_type === 'artist' && user.profile_completion_status === 'incomplete') {
                toast({
                  title: 'Complete your artist profile',
                  description: 'Please complete your artist profile to access the platform',
                  status: 'warning',
                  duration: 3000,
                });
                navigate('/artist-details', { replace: true });
              } else if (user.account_type === 'merchant' && !user.account?.profile?.company_name) {
                toast({
                  title: 'Complete your company profile',
                  description: 'Please complete your company information to access the platform',
                  status: 'warning',
                  duration: 3000,
                });
                navigate('/merchant/company-info', { replace: true });
              } else {
                navigate('/', { replace: true });
              }
            } catch (err) {
              console.error('Failed to check user status after OTP verification:', err);
              navigate('/', { replace: true });
            }
          }
        } catch (error) {
          toast({
            title: 'Invalid verification code',
            description: 'Please check your code and try again',
            status: 'error',
            duration: 3000,
          });
        }
      })}>
        <FormControl isInvalid={errors.otp}>
          <HStack
            spacing={{ base: 1, sm: 2, md: 3 }}
            justify="center"
            mt={3}
            w="100%"
            maxW="400px"
            mx="auto"
          >
            {[0, 1, 2, 3, 4, 5].map((index) => (
              <Input
                key={index}
                id={`otp-${index}`}
                maxLength={1}
                width={{ base: "40px", sm: "45px", md: "50px" }}
                height={{ base: "40px", sm: "45px", md: "50px" }}
                minWidth={{ base: "35px", sm: "40px" }}
                textAlign="center"
                fontSize={{ base: "lg", sm: "xl" }}
                fontWeight="bold"
                variant="outline"
                borderColor="dark.900"
                color="dark.900"
                bg="white"
                _focus={{
                  borderColor: "dark.900",
                  boxShadow: "0 0 0 2px var(--chakra-colors-dark-900)",
                  bg: "primary.50"
                }}
                _hover={{
                  borderColor: "dark.900"
                }}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  e.target.value = value;

                  // Build complete OTP from all boxes
                  let completeOtp = '';
                  for (let i = 0; i <= 5; i++) {
                    const input = document.getElementById(`otp-${i}`);
                    if (input) {
                      completeOtp += input.value || '';
                    }
                  }

                  // Update the form value
                  setValue('otp', completeOtp);

                  // Auto-focus next box
                  if (value && index < 5) {
                    const nextInput = document.getElementById(`otp-${index + 1}`);
                    if (nextInput) nextInput.focus();
                  }
                }}
                onKeyDown={(e) => {
                  // Handle backspace to go to previous box
                  if (e.key === 'Backspace' && !e.target.value && index > 0) {
                    const prevInput = document.getElementById(`otp-${index - 1}`);
                    if (prevInput) prevInput.focus();
                  }
                }}
                onPaste={(e) => {
                  e.preventDefault();
                  const paste = e.clipboardData.getData('text').replace(/[^0-9]/g, '').slice(0, 6);

                  // Fill all boxes with pasted content
                  for (let i = 0; i < 6; i++) {
                    const input = document.getElementById(`otp-${i}`);
                    if (input) {
                      input.value = paste[i] || '';
                    }
                  }

                  // Update form value
                  setValue('otp', paste);

                  // Focus the last filled box or the first empty one
                  const lastIndex = Math.min(paste.length, 5);
                  const targetInput = document.getElementById(`otp-${lastIndex}`);
                  if (targetInput) targetInput.focus();
                }}
              />
            ))}
          </HStack>
          <input
            type="hidden"
            {...register("otp", {
              required: "Verification code is required",
              pattern: {
                value: /^[0-9]{6}$/,
                message: "Enter a 6-digit code"
              }
            })}
          />
          <FormErrorMessage>
            {errors.otp && errors.otp.message}
          </FormErrorMessage>
        </FormControl>

        <Button
          type="submit"
          width="100%"
          variant="solid"
          size="md"
          mt={6}
          isLoading={isSubmitting}
          loadingText="Verifying..."
        >
          Verify Account
        </Button>
      </form>

      <Button
        variant="ghost"
        size="md"
        color="dark.900"
        mt={4}
        isLoading={isResendingOTP}
        loadingText="Sending..."
        onClick={async () => {
          setIsResendingOTP(true);
          try {
            await axiosInstance.post('/auth/resend-otp', {
              user_id: otpData.userId,
              email: otpData.email
            });
            toast({
              title: 'OTP Resent',
              description: 'Check your console/logs for the new verification code',
              status: 'success',
              duration: 3000,
            });
          } catch (err) {
            toast({
              title: 'Failed to resend OTP',
              description: 'Please try again',
              status: 'error',
              duration: 3000,
            });
          } finally {
            setIsResendingOTP(false);
          }
        }}
      >
        Resend Code
      </Button>
    </>
  );

  return (
    <Flex {...containerStyle}>
      {/* Full-screen OAuth Loading Overlay */}
      {isOAuthLoading && (
        <Box
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="rgba(0, 0, 0, 0.7)"
          display="flex"
          alignItems="center"
          justifyContent="center"
          zIndex={9999}
        >
          <VStack spacing={4}>
            <Spinner
              thickness="4px"
              speed="0.65s"
              emptyColor="gray.200"
              color="dark.900"
              size="xl"
            />
            <Text color="white" fontSize="lg" fontWeight="medium">
              {oauthLoadingMessage}
            </Text>
          </VStack>
        </Box>
      )}

      <Flex {...formBoxStyle}>
        {/* Mobile redirect banner */}
        {isMobileRedirect && (
          <Box
            w="100%"
            bg="brand.500"
            color="white"
            p={3}
            mb={4}
            borderRadius="md"
            textAlign="center"
          >
            <Text fontSize="sm" fontWeight="bold">
              📱 Completing registration from mobile app
            </Text>
            <Text fontSize="xs" mt={1}>
              We'll redirect you back to the app when finished
            </Text>
          </Box>
        )}

        {currentStep === 'login' && renderLoginStep()}
        {currentStep === 'register' && renderRegisterStep()}
        {currentStep === 'role-selection' && renderRoleSelectionStep()}
        {currentStep === 'artist-details' && renderArtistDetailsStep()}
        {currentStep === 'otp-verification' && renderOTPStep()}
      </Flex>
    </Flex>
  );
};

export default AuthFlow;